import { Decimal } from 'decimal.js';

/** 顯示上比照法幣處理的穩定幣（最多 2 位小數） */
export const STABLECOIN_CURRENCIES: readonly string[] = ['USDT', 'USDC'];

/** 是否為穩定幣 */
export function isStablecoin(currency: unknown): boolean {
  return STABLECOIN_CURRENCIES.includes(String(currency ?? '').toUpperCase());
}

/**
 * 收銀台金額是否以 2 位小數顯示：法幣與穩定幣是，其餘加密貨幣沿用各幣種規則。
 *
 * @param currency 幣別代碼
 * @param isDigital 幣別設定中的 isDigital
 */
export function isTwoDecimalCurrency(currency: unknown, isDigital: boolean): boolean {
  return !isDigital || isStablecoin(currency);
}

/** 法幣金額的小數位數 */
export const FIAT_AMOUNT_DECIMALS = 2;

/**
 * 加密貨幣金額的小數位數：穩定幣比照法幣 2 位，其餘沿用呼叫端原本的位數。
 *
 * @param currency 幣別代碼
 * @param defaultDecimals 非穩定幣時使用的位數
 */
export function cryptoAmountDecimals(currency: unknown, defaultDecimals: number): number {
  return isStablecoin(currency) ? FIAT_AMOUNT_DECIMALS : defaultDecimals;
}

/**
 * 加密貨幣紅利門檻的小數位數，與 cashierAmount 顯示位數一致：穩定幣 2、BTC/ETH 8、其餘 6。
 * 門檻需以此位數無條件進位，確保畫面顯示的金額實際達得到門檻。
 */
export function cryptoBonusMinimumDecimals(currency: unknown): number {
  if (isStablecoin(currency)) return 2;

  const code = String(currency ?? '').toUpperCase();
  return code === 'BTC' || code === 'ETH' ? 8 : 6;
}

/**
 * 無條件進位到指定小數位，扣除浮點誤差避免 `20.000000000000004` 這類運算殘差被進位成 `20.01`。
 */
export function roundUpAmount(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  const precisionOffset = Number.EPSILON * Math.max(1, Math.abs(value));

  return Math.ceil((value - precisionOffset) * factor) / factor;
}

/**
 * 四捨五入到指定小數位（恰好為半值時進位）：`20.123` → `20.12`、`20.125` → `20.13`。
 * 用於匯率換算結果與顯示金額。
 */
export function roundAmount(value: number, decimals: number): number {
  if (!Number.isFinite(value)) return value;
  return new Decimal(value).toDecimalPlaces(decimals, Decimal.ROUND_HALF_UP).toNumber();
}

/**
 * 無條件捨去到指定小數位，用於上限類的值（例如 Max 金額），避免進位後超出實際可用額度。
 */
export function truncateAmount(value: number, decimals: number): number {
  if (!Number.isFinite(value)) return value;
  return new Decimal(value).toDecimalPlaces(decimals, Decimal.ROUND_DOWN).toNumber();
}

/**
 * 將法幣/穩定幣金額格式化為顯示字串：四捨五入到 2 位小數、移除結尾的 0、保留千分位。
 * 用於無法套用 cashierAmount pipe 的字串組合（例如紅利選項文字）：`20.000000` → `20`、`1000.905` → `1,000.91`
 */
export function formatTwoDecimalAmount(value: unknown): string {
  const numeric = Number(value);
  const rounded = Number.isFinite(numeric) ? roundAmount(numeric, FIAT_AMOUNT_DECIMALS) : 0;
  return rounded.toLocaleString('en-US', { maximumFractionDigits: FIAT_AMOUNT_DECIMALS });
}

/**
 * 移除已格式化金額字串小數部分結尾的 0，不影響千分位與 M 簡寫。
 * 例：`20.00` → `20`、`1,000.50` → `1,000.5`、`1,234.5M` 不變
 */
export function trimTrailingZeros(formatted: string): string {
  return formatted.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
}
